/**
 * fetchConversationLookup — REST call for GET /admin/conversation/:id,
 * the admin conversation deep-dive lookup
 * (specs/open/conversation-id-lookup.md §5/§6).
 *
 * Unlike fetchAdminFeedback's fetchJson-based call, this has a
 * distinguishable failure mode the caller must react to differently (404
 * "not found or too old to retrieve" vs. every other error, per §5's two
 * non-success states) -- mirrors createFeedbackTicket.ts's own
 * custom-error-class pattern rather than fetchJson's throw-status-in-
 * message-only contract, for the same reason that file states.
 *
 * ConversationLookupTurn is a frontend-local copy of index.ts's response
 * shape -- there is no shared types package between these two repos, so
 * this must be kept in sync by hand if the backend's shape changes.
 * Deliberately has no userAlias field -- the backend never sends one
 * (spec §6, Decisions Log #7).
 */
import { authService } from './auth';
import { API_URL } from './common/constants';
import type { PersistedToolCall } from '@amzn/soda-brain-protocol';

/** Mirrors TurnManifest's status union (execution-types.ts) -- the four values a turn's manifest can carry. */
export type ConversationLookupTurnStatus = 'RUNNING' | 'PAUSED' | 'RESUMING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface ConversationLookupTurn {
  turnNumber: number;
  status?: ConversationLookupTurnStatus;
  userMessage?: string;
  toolCalls?: PersistedToolCall[];
  answer?: string;
  outcomeSummary?: string;
}

interface ConversationLookupResponse {
  turns: ConversationLookupTurn[];
}

/**
 * Thrown for any non-2xx response. `status` lets the caller distinguish
 * 404 (not found / too old to retrieve, per spec §5) from every other
 * error without parsing `message` text.
 */
export class FetchConversationLookupError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'FetchConversationLookupError';
  }
}

/**
 * Looks up a conversation's full turn-by-turn transcript by conversationId.
 * Throws FetchConversationLookupError on any non-2xx response, or a plain
 * Error if there's no valid auth token. The caller is responsible for
 * trimming conversationId before calling this (spec §5 -- trimming is a
 * client responsibility, not this function's).
 */
export async function fetchConversationLookup(conversationId: string): Promise<ConversationLookupTurn[]> {
  const token = authService.getIdToken();
  if (!token) throw new Error('Unable to look up conversation: no valid auth token');

  const res = await fetch(`${API_URL}/admin/conversation/${encodeURIComponent(conversationId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new FetchConversationLookupError(
      res.status,
      typeof data.error === 'string' ? data.error : `Unable to look up conversation: server responded ${res.status}`,
    );
  }
  return (data as ConversationLookupResponse).turns;
}
